import { getAppConfig } from './app-config';

type Service = 'assistants' | 'agents' | 'mcp';

function getBaseUrl(service: Service): string {
  const config = getAppConfig();
  switch (service) {
    case 'assistants':
      return `${config.assistantsBackendUrl}/api/v1`;
    case 'agents':
      return `${config.agentsBackendUrl}/api/v1`;
    case 'mcp':
      return `${config.mcpBackendUrl}/api/v1`;
  }
}

export const endpoints = {
  // Agent execution (agents-backend)
  // POST /api/v1/agents — execute agent (SSE stream)
  agentExecute: () => `${getBaseUrl('agents')}/agents`,

  // Chat endpoint (agents-backend) — alias for agent execution
  chat: () => `${getBaseUrl('agents')}/agents`,

  // Chat tool result (agents-backend)
  chatToolResult: () => `${getBaseUrl('agents')}/agents/tool-result`,

  // Sessions (agents-backend)
  sessions: {
    list: () => `${getBaseUrl('agents')}/sessions`,
    getById: (id: string) => `${getBaseUrl('agents')}/sessions/${id}`,
    delete: (id: string) => `${getBaseUrl('agents')}/sessions/${id}`,
    rename: (id: string) => `${getBaseUrl('agents')}/sessions/${id}/name`,
    update: (id: string) => `${getBaseUrl('agents')}/sessions/${id}`,
    stop: (id: string) => `${getBaseUrl('agents')}/sessions/${id}/stop`,
    stream: (id: string) => `${getBaseUrl('agents')}/sessions/${id}/stream`,
    connectors: (id: string) => `${getBaseUrl('agents')}/sessions/${id}/connectors`,
    connector: (sessionId: string, connectorId: string) =>
      `${getBaseUrl('agents')}/sessions/${sessionId}/connectors/${connectorId}`,
  },

  // Workspaces (assistants-backend)
  workspaces: {
    list: () => `${getBaseUrl('assistants')}/workspaces`,
    getById: (id: string) => `${getBaseUrl('assistants')}/workspaces/${id}`,
    create: () => `${getBaseUrl('assistants')}/workspaces`,
    update: (id: string) => `${getBaseUrl('assistants')}/workspaces/${id}`,
    delete: (id: string) => `${getBaseUrl('assistants')}/workspaces/${id}`,
    uploadFile: (id: string) => `${getBaseUrl('assistants')}/workspaces/${id}/upload/file`,
  },

  // Workspace-scoped assistants (assistants-backend)
  // The backend calls them "assistants"; the add-in UI calls them "agents"
  agents: {
    list: (wid: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/assistants`,
    getById: (wid: string, id: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/assistants/${id}`,
    create: (wid: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/assistants`,
    update: (wid: string, id: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/assistants/${id}`,
    delete: (wid: string, id: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/assistants/${id}`,
    connectors: (wid: string, id: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/assistants/${id}/connectors`,
    skills: (wid: string, id: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/assistants/${id}/skills`,
  },

  // Non-workspace assistants (assistants-backend) — for global/shared assistants
  assistants: {
    list: () => `${getBaseUrl('assistants')}/assistants`,
    getById: (id: string) => `${getBaseUrl('assistants')}/assistants/${id}`,
    create: () => `${getBaseUrl('assistants')}/assistants`,
    update: (id: string) => `${getBaseUrl('assistants')}/assistants/${id}`,
    delete: (id: string) => `${getBaseUrl('assistants')}/assistants/${id}`,
    withConnectors: (id: string) => `${getBaseUrl('assistants')}/assistants/with-connectors/${id}`,
  },

  // Connectors (assistants-backend)
  connectors: {
    list: () => `${getBaseUrl('assistants')}/connectors`,
    getById: (id: string) => `${getBaseUrl('assistants')}/connectors/${id}`,
    create: () => `${getBaseUrl('assistants')}/connectors`,
    update: (id: string) => `${getBaseUrl('assistants')}/connectors/${id}`,
    delete: (id: string) => `${getBaseUrl('assistants')}/connectors/${id}`,
    connect: (id: string) => `${getBaseUrl('assistants')}/connectors/${id}/connect`,
    reset: (id: string) => `${getBaseUrl('assistants')}/connectors/${id}/reset`,
    assistants: (id: string) => `${getBaseUrl('assistants')}/connectors/${id}/assistants`,
    tools: (id: string) => `${getBaseUrl('mcp')}/connectors/${id}/tools`,
    discoverTools: (id: string) => `${getBaseUrl('mcp')}/connectors/${id}/tools`,
    test: (id: string) => `${getBaseUrl('assistants')}/connectors/${id}/test`,
    agents: (id: string) => `${getBaseUrl('assistants')}/connectors/${id}/assistants`,
  },

  // Connector Templates (assistants-backend)
  connectorTemplates: {
    list: () => `${getBaseUrl('assistants')}/connector/templates`,
    getById: (id: string) => `${getBaseUrl('assistants')}/connector/templates/${id}`,
    instantiate: (id: string) => `${getBaseUrl('assistants')}/connectors/templates/${id}/connectors`,
  },

  // Skills — admin endpoints (assistants-backend)
  skills: {
    list: () => `${getBaseUrl('assistants')}/skills`,
    getById: (id: string) => `${getBaseUrl('assistants')}/skills/${id}`,
    available: (wid: string) => `${getBaseUrl('assistants')}/skills/available?workspaceId=${wid}`,
    create: () => `${getBaseUrl('assistants')}/skills`,
    update: (id: string) => `${getBaseUrl('assistants')}/skills/${id}`,
    delete: (id: string) => `${getBaseUrl('assistants')}/skills/${id}`,
    upload: () => `${getBaseUrl('assistants')}/skills/upload`,
    content: (id: string) => `${getBaseUrl('assistants')}/skills/${id}/content`,
    import: () => `${getBaseUrl('assistants')}/skills/import`,
    importZip: () => `${getBaseUrl('assistants')}/skills/import-zip`,
  },

  // Workspace-scoped skills (assistants-backend) — for user skill CRUD
  workspaceSkills: {
    list: (wid: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/skills`,
    getById: (wid: string, id: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/skills/${id}`,
    create: (wid: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/skills`,
    upload: (wid: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/skills/upload`,
    update: (wid: string, id: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/skills/${id}`,
    delete: (wid: string, id: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/skills/${id}`,
    download: (wid: string, id: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/skills/${id}/download`,
  },

  // Provider Templates (assistants-backend) — available provider types
  providerTemplates: {
    list: () => `${getBaseUrl('assistants')}/provider-templates`,
    models: (providerId: string) => `${getBaseUrl('assistants')}/provider-models/${providerId}`,
  },

  // Workspace-scoped Providers (assistants-backend) — configured provider instances
  providers: {
    list: (wid: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/providers`,
    getById: (wid: string, id: number) => `${getBaseUrl('assistants')}/workspaces/${wid}/providers/${id}`,
    create: (wid: string) => `${getBaseUrl('assistants')}/workspaces/${wid}/providers`,
    update: (wid: string, id: number) => `${getBaseUrl('assistants')}/workspaces/${wid}/providers/${id}`,
    delete: (wid: string, id: number) => `${getBaseUrl('assistants')}/workspaces/${wid}/providers/${id}`,
    models: (wid: string, providerId: number) => `${getBaseUrl('assistants')}/workspaces/${wid}/providers/models/${providerId}`,
    createCustomModel: (wid: string, id: number) => `${getBaseUrl('assistants')}/workspaces/${wid}/providers/${id}/custom-models`,
    updateCustomModel: (wid: string, id: number, modelId: number) => `${getBaseUrl('assistants')}/workspaces/${wid}/providers/${id}/custom-models/${modelId}`,
    deleteCustomModel: (wid: string, id: number, modelId: number) => `${getBaseUrl('assistants')}/workspaces/${wid}/providers/${id}/custom-models/${modelId}`,
  },

  // Health (both backends)
  health: {
    assistants: () => `${getBaseUrl('assistants')}/health`,
    agents: () => `${getBaseUrl('agents')}/health`,
  },
};
